import { FC, useEffect } from 'react';
import { Redirect, Route, RouteComponentProps, Switch, useLocation } from 'react-router-dom';
import ClientProvider from 'core/ClientProvider';
import { getSubdomain } from 'utils/helpers/routing';
import PrivateRoute from './PrivateRoute';

import commonRoutes, { IRouteConfig } from './config/common-routes';
import operatorRoutes from './config/operator-routes';
import clientRoutes from './config/client-routes';

const Routes: FC = () => {
  // Scroll to top on location change
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const renderRouteComponent = (route: IRouteConfig, props: RouteComponentProps) => {
    if (route.isProvidingClient)
      return (
        <ClientProvider>
          <route.component {...props} />
        </ClientProvider>
      );

    return <route.component {...props} />;
  };

  const renderRoutes = (routes: IRouteConfig[]) => {
    return routes.map((route: IRouteConfig, index: number) => {
      if (route.isPrivate)
        return (
          <PrivateRoute
            key={index}
            path={route.path}
            isExact={route.isExact}
            is2FaProtected={route.is2FaProtected}
            permissionGrantingAccess={route.permissionGrantingAccess}
            render={props => renderRouteComponent(route, props)}
          />
        );
      return (
        <Route
          key={index}
          path={route.path}
          exact={route.isExact}
          render={props => renderRouteComponent(route, props)}
        />
      );
    });
  };

  const isOperatorMode = getSubdomain() === 'operators';

  return (
    <Switch>
      {/**
       * Render Common Routes - Login, 404, etc.
       */}
      {renderRoutes(commonRoutes)}

      {/**
       * If Operator Mode, render Operator routes
       */}
      {isOperatorMode && renderRoutes(operatorRoutes)}

      {/**
       * If Client Mode, render Client routes
       */}
      {!isOperatorMode && renderRoutes(clientRoutes)}

      {/**
       * In all other cases if the path doesn't exist
       * in the routes config - redirect to 404 page
       */}
      <Route path="*">
        <Redirect to="/404" />
      </Route>
    </Switch>
  );
};

export default Routes;
